/**
 * React hooks for loading and querying data from IndexedDB
 * Alternative to in-memory approach - uses ~10MB RAM vs 800MB
 */

import { useQuery } from '@tanstack/react-query'
import { postNamedQuery } from '../../../../../service'
import { executeWithConcurrency } from '../utils/concurrencyLimit'
import {
  getCacheMetadata,
  isCacheFresh,
  queryPreBuiltHierarchy,
  queryTopLevelGroups,
  queryGroupChildren,
  initializeCacheForLoading,
  storeChunk
} from './reportPersistence'

/**
 * Hook to fetch totals row from server
 * Separate query that returns aggregated totals
 */
export function useReportTotals (query, userId, enabled = true) {
  const queryKeyString = JSON.stringify(query || {})

  return useQuery({
    queryKey: ['ddp/indexeddb/totals', queryKeyString, userId],
    queryFn: async () => {
      const { data: totalsResult } = await postNamedQuery('coreData', 'execute-data-pipeline', {
        pipelineCode: query.pipeline,
        variables: {
          filters: {
            ...(query?.variables || {}),
            query_type: 'total'
          }
        }
      })

      return totalsResult?.data || []
    },
    enabled: enabled && !!query && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000
  })
}

/**
 * Hook to load report data into IndexedDB
 * First checks cache, then fetches from server if needed
 *
 * @param {Object} query - Query configuration
 * @param {string} userId - Current user ID
 * @param {Function} onProgress - Progress callback
 * @param {number} maxAge - Cache max age in ms (default: 1 hour)
 * @param {number} concurrency - Parallel requests (default: 4)
 * @param {number} chunkSize - Rows per chunk (default: 10000)
 */
export function useIndexedDBReportLoader (
  query,
  userId,
  onProgress,
  maxAge = 60 * 60 * 1000,
  concurrency = 4,
  chunkSize = 10000
) {
  const queryKeyString = JSON.stringify(query || {})

  return useQuery({
    queryKey: ['ddp/indexeddb/loader', queryKeyString, userId],
    queryFn: async () => {
      console.debug('Checking IndexedDB cache...')

      if (onProgress) {
        onProgress({ stage: 'checking', message: 'Checking cache...' })
      }

      // Check if cache exists and is fresh
      const isFresh = await isCacheFresh(
        query.pipeline,
        query.variables,
        userId,
        maxAge
      )

      if (isFresh) {
        console.debug('Cache is fresh, using IndexedDB data')
        const metadata = await getCacheMetadata(
          query.pipeline,
          query.variables,
          userId
        )

        if (onProgress) {
          onProgress({
            stage: 'complete',
            message: `Loaded from cache (${metadata.row_count.toLocaleString()} rows)`
          })
        }

        return {
          source: 'cache',
          metadata
        }
      }

      // Cache miss or stale - fetch from server
      console.debug('Cache miss or stale, fetching from server...')

      if (onProgress) {
        onProgress({ stage: 'counting', rowsLoaded: 0, message: 'Counting rows...' })
      }

      // Step 1: Get row count
      const { data: countResult } = await postNamedQuery('coreData', 'execute-data-pipeline', {
        pipelineCode: query.pipeline,
        variables: {
          filters: {
            ...(query?.variables || {}),
            query_type: 'row_count'
          }
        }
      })

      const totalRows = countResult?.rowCount || countResult?.data?.rowCount || 0
      console.debug(`Total rows to fetch: ${totalRows}`)

      if (totalRows === 0) {
        return { source: 'server', metadata: { row_count: 0 } }
      }

      // Step 2: Initialize cache for incremental loading
      const numChunks = Math.ceil(totalRows / chunkSize)
      await initializeCacheForLoading(
        query.pipeline,
        query.variables,
        userId,
        totalRows,
        numChunks
      )

      // Step 3: Fetch chunks and store incrementally
      console.debug(`Fetching ${numChunks} chunks with concurrency: ${concurrency}`)

      const chunkTasks = Array.from({ length: numChunks }, (_, chunkIndex) => {
        return async () => {
          const skip = chunkIndex * chunkSize
          const take = Math.min(chunkSize, totalRows - skip)

          const { data: chunkData } = await postNamedQuery('coreData', 'execute-data-pipeline', {
            pipelineCode: query.pipeline,
            variables: {
              filters: {
                ...(query?.variables || {}),
                query_type: 'full',
                detailTake: take,
                detailSkip: skip
              }
            }
          })

          const pageData = chunkData?.data || []

          // Store chunk immediately in IndexedDB (incremental!)
          const { rowCount, chunksLoaded } = await storeChunk(
            query.pipeline,
            query.variables,
            userId,
            pageData,
            chunkIndex
          )

          if (onProgress) {
            onProgress({
              stage: 'loading',
              rowsLoaded: rowCount,
              totalRows,
              chunksLoaded,
              totalChunks: numChunks,
              message: `Loaded ${rowCount.toLocaleString()} of ${totalRows.toLocaleString()} (chunk ${chunksLoaded}/${numChunks})...`
            })
          }

          console.debug(`Chunk ${chunkIndex + 1}/${numChunks} stored in IndexedDB (${pageData.length} rows)`)

          return pageData.length
        }
      })

      await executeWithConcurrency(chunkTasks, concurrency)

      console.debug('All chunks stored in IndexedDB')

      // Step 4: Build hierarchy from IndexedDB data
      const { storeHierarchyFromIndexedDB } = await import('./reportPersistence')
      const result = await storeHierarchyFromIndexedDB(
        query.pipeline,
        query.variables,
        userId,
        query.hierarchy,
        onProgress
      )

      if (onProgress) {
        onProgress({
          stage: 'complete',
          message: `Loaded ${result.rowCount.toLocaleString()} rows with ${result.groupCount.toLocaleString()} groups`
        })
      }

      return {
        source: 'server',
        metadata: {
          row_count: result.rowCount,
          updated_at: Date.now(),
          status: 'complete'
        }
      }
    },
    staleTime: maxAge,
    cacheTime: maxAge * 2,
    refetchOnWindowFocus: false
  })
}

/**
 * Hook to query paginated data from IndexedDB
 * Uses pre-built hierarchy for instant loading (no grouping needed)
 *
 * @param {Object} query - Query configuration
 * @param {string} userId - Current user ID
 * @param {Object} pageOptions - Pagination options
 * @param {boolean} enabled - Whether to run the query (wait for loader to complete)
 */
export function useIndexedDBPageQuery (query, userId, pageOptions, enabled = true) {
  const queryKeyString = JSON.stringify(query || {})
  const pageKeyString = JSON.stringify(pageOptions || {})

  return useQuery({
    queryKey: ['ddp/indexeddb/page', queryKeyString, userId, pageKeyString],
    queryFn: async () => {
      // Try pre-built hierarchy first (FAST!)
      let result = await queryPreBuiltHierarchy(
        query.pipeline,
        query.variables,
        userId,
        pageOptions
      )

      // Fallback: If no pre-built hierarchy, build on-the-fly
      // This happens if using old cache from before hierarchy caching was added
      if (!result || result.total === 0) {
        console.warn('No pre-built hierarchy, falling back to queryTopLevelGroups')
        result = await queryTopLevelGroups(
          query.pipeline,
          query.variables,
          userId,
          query.hierarchy,
          pageOptions
        )
      }

      return {
        data: result.data,
        total: result.total,
        pageIndex: Math.floor((pageOptions?.offset || 0) / (pageOptions?.limit || 250)),
        pageSize: pageOptions?.limit || 250
      }
    },
    enabled: enabled && !!query && !!userId, // Wait for loader to complete
    keepPreviousData: true, // Keep previous page while loading next
    staleTime: 5 * 60 * 1000 // 5 minutes - data is in IndexedDB, cheap to re-query
  })
}

/**
 * Hook to query children for expanded nodes
 *
 * @param {Object} query - Query configuration
 * @param {string} userId - Current user ID
 * @param {string} parentKey - Parent group key
 * @param {boolean} enabled - Whether to run query
 */
export function useIndexedDBChildrenQuery (query, userId, parentKey, enabled) {
  return useQuery({
    queryKey: ['ddp/indexeddb/children', query.pipeline, userId, parentKey],
    queryFn: async () => {
      const children = await queryGroupChildren(
        query.pipeline,
        query.variables,
        userId,
        query.hierarchy,
        parentKey
      )

      return children
    },
    enabled: enabled && !!parentKey,
    staleTime: 5 * 60 * 1000
  })
}
